import type { datadogRum } from '@datadog/browser-rum';
import { beforeSend as sentryBeforeSend } from '@mentimeter/sentry-config';
import { createSentryError } from './utils/create-sentry-error';

type BeforeSend = NonNullable<
  Parameters<typeof datadogRum.init>[0]['beforeSend']
>;
type SentryErrorEvent = Parameters<typeof sentryBeforeSend>[0];
type SentryEventHint = Parameters<typeof sentryBeforeSend>[1];

/**
 * Access to every event collected by the RUM SDK before they
 * are sent to Datadog.
 *
 * It allows:
 * - Enrich your RUM events with additional context attributes.
 * - Modify your RUM events to modify their content, or redact
 *   sensitive sequences (see the list of editable properties).
 * - Discard selected RUM events.
 *
 * @see [Enrich And Control Browser RUM Data With beforeSend](https://docs.datadoghq.com/real_user_monitoring/guide/enrich-and-control-rum-data).
 * @see [Discard a frontend error](https://docs.datadoghq.com/real_user_monitoring/guide/enrich-and-control-rum-data/?tab=event#discard-a-frontend-error).
 */
export const beforeSend: BeforeSend = (event, context) => {
  if (event.type !== 'error') {
    return true;
  }

  const environment =
    (process.env.NODE_ENV === 'development' ? 'dev' : process.env.NODE_ENV) ??
    'prod';

  const originalException = createSentryError(event, context);

  const errorEvent: SentryErrorEvent = {
    environment,
    type: undefined,
    transaction:
      typeof event.id === 'string' && event.id ? event.id : event.view.id,
    request: {
      url:
        event.error?.resource?.url ?? event.view?.url ?? window.location.href,
    },
  };

  const eventHint: SentryEventHint = {
    originalException,
  };

  /**
   * Mimics the arguments passed to the Sentry `beforeSend` function.
   * - TODO: Fix the arguments so that it can identify a "bad stack trace".
   */
  const result = sentryBeforeSend(errorEvent, eventHint, originalException);
  return !!result;
};
